import React, { useState } from "react"
import { Switch, Redirect, useParams } from "react-router-dom"
import { Tabs, Grid, Box, Divider, Hidden, Drawer, List, IconButton, makeStyles } from "@material-ui/core"
import { MailOutline, Menu as MenuIcon } from "@material-ui/icons"
import Config from "react-global-configuration"
import AccreditationTypes from "./AccreditationTypes"
import Billing from "./Billing"
import Devices from "./Devices"
import IdentityProviders from "./IdentityProviders"
import Integrations from "./Integrations"
import Organisation from "./Organisation"
import Locations from "./Locations"
import Training from "./Training"
import Accreditation from "./Accreditation"
import Groups from "./Groups"
import Notifications from "./SimpleNotifications"
import Profile from "./Profile"
import {
  AreaHeader,
  Blockquote,
  Content,
  ErrorBoundary,
  Icon,
  MainHeading,
  ModuleHeading,
  NavLinkTab,
  PrivateRoute,
  RowBox,
  SubNavigation,
  SubNavigationListItem,
  SubNavigationListItemLink,
  TextDivider,
} from "../../components"
import { UserPanel } from "../../components/UserPanel/UserPanel"
import { useAuth } from "../../services"
import { useQueryAdminableUser } from "../../data"
import Triggers from "./Triggers"

const useSubNavStyles = makeStyles((theme) => ({
  filterButton: {
    paddingTop: ({ xs }) => (xs ? 6 : "inherit"),
    paddingBottom: ({ xs }) => (xs ? 6 : "inherit"),
  },
  supportLink: {
    color: theme.palette.primary.main,
  },
}))

const ForceBillingNotice = () => {
  const {
    settings: {
      organisation: { disabled: forceBilling },
    },
  } = useAuth()

  if (!forceBilling) return null

  return (
    <Box pb={2}>
      <Blockquote
        icon={<Icon name="warning" />}
        title="Payment required"
        body={
          <Box>
            Your account is currently restricted. Please visit the billing portal below to reactivate your subscription.
            <br />
            <br />
            After payment has been made it may take several hours for access to be restored.
          </Box>
        }
        color="warning"
      />
    </Box>
  )
}

const AccountRoutes = () => (
  <ErrorBoundary>
    <Switch>
      <PrivateRoute exact path="/account/profile" component={Profile} />
      <PrivateRoute exact path="/account/notifications" component={Notifications} requires="jobprocess_read_all" />
      <PrivateRoute exact path="/account/training" component={Training} />
      <PrivateRoute exact path="/account/accreditation" component={Accreditation} />
      <PrivateRoute exact path="/account/organisation" component={Organisation} requires="organisation_update" />
      <PrivateRoute exact path="/account/locations" component={Locations} />
      <PrivateRoute exact path="/account/regions" component={Locations} />
      <PrivateRoute exact path="/account/groups" component={Groups} requires="group_update" />
      <PrivateRoute exact path="/account/accreditation-types" component={AccreditationTypes} />
      <PrivateRoute exact path="/account/billing" component={Billing} requires="billing_read" />
      <PrivateRoute
        exact
        path="/account/devices"
        component={Devices}
        requires={["devices_update_locations", "devices_update_all"]}
      />
      <PrivateRoute
        exact
        path="/account/identity-providers"
        component={IdentityProviders}
        requires={["identity_provider_update"]}
      />
      <PrivateRoute
        exact
        path="/account/integrations/provider/:integrationProviderId/connect"
        component={Integrations}
        requires="integration_update"
      />
      <PrivateRoute
        exact
        path="/account/integrations/provider/:integrationProviderId"
        component={Integrations}
        requires="integration_read"
      />
      <PrivateRoute
        exact
        path="/account/integrations/:id"
        component={Integrations}
        requires="integration_read"
        feature="integrations"
      />
      <PrivateRoute
        exact
        path="/account/integrations"
        component={Integrations}
        requires="integration_read"
        feature="integrations"
      />
      <PrivateRoute
        exact
        path="/account/triggers/:id"
        component={Triggers}
        requires="trigger_read"
        feature="triggers"
      />
      <PrivateRoute exact path="/account/triggers" component={Triggers} requires="trigger_read" feature="triggers" />
      <Redirect to="/account/profile" />
    </Switch>
  </ErrorBoundary>
)

const AccountUser = () => {
  const params = useParams()
  const {
    principal,
    hasPermission,
    hasFeature,
    settings: {
      organisation: { disabled: forceBilling },
    },
  } = useAuth()
  const { data: userData } = useQueryAdminableUser({
    variables: { id: principal.userID, organisation: principal.organisation },
  })

  const NavTab = ({ value }) => <NavLinkTab label={getHeading(value)} base="/account/" value={value} />

  return (
    <ErrorBoundary>
      <AreaHeader title="Settings" titleIcon="settings-light" />

      <Content full start mb={5}>
        <Grid container direction="row">
          <Hidden mdDown>
            <Grid item md="auto">
              {userData && <UserPanel user={userData.adminableUser} mt={1} />}
            </Grid>
          </Hidden>
          <Grid item xs>
            <ForceBillingNotice />
            <PageHeadingAndMobileSubNav />
            <Hidden mdDown>
              <Tabs
                variant="standard"
                value={forceBilling ? "billing" : params.page || "profile"}
                indicatorColor="primary"
                textColor="primary"
                aria-label="Select account setting area"
              >
                <NavTab value="profile" />
                {hasFeature("training") && <NavTab value="training" />}
                {hasFeature("accreditation") && <NavTab value="accreditation" />}
                <NavTab value="locations" />
                {hasPermission("group_update") && <NavTab value="groups" />}
                {hasPermission("organisation_update") && <NavTab value="organisation" />}
                {hasPermission("jobprocess_read_all") && <NavTab value="notifications" />}
                {hasPermission("billing_read") && <NavTab value="billing" />}
                {hasPermission(["devices_update_locations", "devices_update_all"]) && <NavTab value="devices" />}
                {hasFeature("identity_providers") && hasPermission("identity_provider_update") && (
                  <NavTab to="identity-providers" />
                )}
              </Tabs>
            </Hidden>

            <Divider />

            <Box pt={5}>
              <AccountRoutes />
            </Box>
          </Grid>
        </Grid>
      </Content>
    </ErrorBoundary>
  )
}

const AccountAdmin = () => (
  <ErrorBoundary>
    <AreaHeader title="Settings" subTitle="Administrator" titleIcon="settings-light" />

    <Content full start>
      <Grid container direction="row">
        <Grid item xs={12} lg={3}>
          <AdminSubNav />
        </Grid>
        <Grid item xs={12} lg={9}>
          <Grid item xs>
            <ForceBillingNotice />

            <PageHeadingAndMobileSubNav admin />

            <AccountRoutes />
          </Grid>
        </Grid>
      </Grid>
    </Content>
  </ErrorBoundary>
)

const AdminSubNav = () => {
  const { page } = useParams()
  const {
    hasPermission,
    hasFeature,
    permissionGroups: { adminAccreditations },
  } = useAuth()

  const NavItem = ({ to }) => <SubNavigationListItem to={`/account/${to}`} text={getHeading(to)} active={page === to} />

  return (
    <Hidden mdDown>
      <SubNavigation>
        <List>
          <NavItem to="profile" />
          {hasPermission("jobprocess_read_all") && <NavItem to="notifications" />}
          {hasFeature("training") && <NavItem to="training" />}
          {hasFeature("accreditation") && <NavItem to="accreditation" />}
          <Box my={1}>
            <TextDivider />
          </Box>
          {hasPermission("organisation_update") && <NavItem to="organisation" />}
          <NavItem to="locations" />
          {hasPermission("group_update") && <NavItem to="groups" />}
          {hasFeature("accreditation") && hasPermission(adminAccreditations) && <NavItem to="accreditation-types" />}
          {hasPermission("billing_read") && <NavItem to="billing" />}
          {hasPermission(["devices_update_locations", "devices_update_all"]) && <NavItem to="devices" />}
          {hasFeature("identity_providers") && hasPermission("identity_provider_update") && (
            <NavItem to="identity-providers" />
          )}
          {hasFeature("integrations") && hasPermission("integration_read") && <NavItem to="integrations" />}
          {hasFeature("triggers") && hasPermission("trigger_read") && <NavItem to="triggers" />}
        </List>
      </SubNavigation>
    </Hidden>
  )
}

const PageHeadingAndMobileSubNav = ({ admin = false }) => {
  const classes = useSubNavStyles()
  const { page } = useParams()
  const configLinks = Config.get("links")
  const { clientKiosk: kiosk } = Config.get()
  const {
    hasPermission,
    hasFeature,
    settings: {
      organisation: { disabled: forceBilling },
    },
    permissionGroups: { adminAccreditations },
  } = useAuth()
  const [open, setOpen] = useState(false)

  const handleToggleOpen = () => setOpen(!open)

  const handleClick = () => {
    setOpen(false)
  }

  const NavItem = ({ to }) => (
    <SubNavigationListItem
      to={`/account/${to}`}
      text={getHeading(to)}
      active={page === to}
      onClick={() => handleClick()}
    />
  )

  const heading = getHeading(forceBilling ? "billing" : page)

  return (
    <>
      {admin && (
        <Hidden mdDown>
          <MainHeading mb={3.75} ml={0}>
            {heading}
          </MainHeading>
        </Hidden>
      )}
      <Hidden lgUp>
        <Box display="flex" alignItems="center" direction="row" mb={1}>
          <Box flexGrow={1}>
            <ModuleHeading noMargin>{heading}</ModuleHeading>
          </Box>
          <Box alignSelf="center">
            <IconButton onClick={handleToggleOpen} className={classes.filterButton}>
              <MenuIcon />
            </IconButton>
          </Box>
        </Box>

        <Drawer open={open} onClose={handleToggleOpen}>
          <List>
            <NavItem to="profile" />
            {hasPermission("jobprocess_read_all") && <NavItem to="notifications" />}
            {hasFeature("training") && <NavItem to="training" />}
            {hasFeature("accreditation") && <NavItem to="accreditation" />}
            <Box my={1}>
              <TextDivider />
            </Box>
            {hasPermission("organisation_update") && <NavItem to="organisation" />}
            <NavItem to="locations" />
            {hasPermission("group_update") && <NavItem to="groups" />}
            {hasFeature("accreditation") && hasPermission(adminAccreditations) && <NavItem to="accreditation-types" />}
            {hasPermission("billing_read") && <NavItem to="billing" />}
            {hasPermission(["devices_update_locations", "devices_update_all"]) && <NavItem to="devices" />}
            {hasFeature("identity_providers") && hasPermission("identity_provider_update") && (
              <NavItem to="identity-providers" />
            )}
            {hasFeature("triggers") && hasPermission("trigger_read") && <NavItem to="triggers" />}
            {!kiosk && (
              <Box my={1}>
                <TextDivider />
              </Box>
            )}
            {!kiosk && (
              <SubNavigationListItemLink
                href={`mailto:${configLinks.emailSupport}`}
                rel="noopener noreferrer"
                target="_blank"
              >
                <RowBox className={classes.supportLink}>
                  <MailOutline />
                  <Box ml={1}>Contact support</Box>
                </RowBox>
              </SubNavigationListItemLink>
            )}
          </List>
        </Drawer>
      </Hidden>
    </>
  )
}

const getHeading = (page) => {
  switch (page) {
    case "profile":
      return "Profile"
    case "locations":
    case "regions":
      return "Locations"
    case "training":
      return "Your Training"
    case "accreditation":
      return "Your Accreditations"
    case "groups":
      return "Areas of Work"
    case "accreditation-types":
      return "Accreditation Types"
    case "organisation":
      return "Company"
    case "notifications":
      return "Your Notifications"
    case "billing":
      return "Billing"
    case "devices":
      return "Devices"
    case "identity-providers":
      return "Single Sign-On"
    case "integrations":
      return "Integrations"
    case "triggers":
      return "Triggers"

    default:
      return "Loading..."
  }
}

export { AccountUser, AccountAdmin }
